import { useEffect, useState, createContext, useContext } from "react";
import { tss } from "tss-react/mui";
import { 
    CircularProgress, 
    Typography, 
    Button, 
    Checkbox, 
    Chip, 
    Tooltip,
    MenuItem, 
    Link,
    ListItemText,
    ListItemIcon, 
} from "@mui/material";
import { StorageUtils } from "services/UtilsService";
import { TreeItem, SimpleTreeView } from "@mui/x-tree-view";
import { Scrollbars } from "react-custom-scrollbars";
import { BreadCrumbs, StyledTreeItem, CircularProgressWithLabel } from "components";
import { getCachedData } from "api/Cache";
import { icons } from "constants/viewer";
import { getFileIcon } from "actions/GlobalActions";
import { FoldersActions } from "actions";
import { useHistory } from "react-router-dom";
import * as Utils from "services/UtilsService";
import DeleteItemsPanel from "panels/DeleteItemsPanel";
import EventsEmitter from "events/EventsEmitter";
import { Public } from "@mui/icons-material";
import { ReactComponent as File } from "assets/images/file.svg";
import commonStyles, {
    folderColor,
    headerHeight,
    viewsHeaderHeight,
    rowHeight,
    panelBackgroundColor,
} from "styles/common";
import viewsStyles from "styles/views";
import { useSelector } from 'react-redux';
import useSwalWrapper from 'themes/jumbo/@jumbo/vendors/sweetalert2/hooks';
import _ from 'utils/@lodash';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import { StyledMenu } from 'themes/jumbo/app/_components/_core';
import { getAccessLevel } from "actions/ViewerTools";
import { useVersioning } from "constants/common";
import { Div } from 'themes/jumbo/@jumbo/shared';


const useStyles: any = tss.create(({theme}) => ({
    header: {
        position: "relative",
        flexDirection: "row",
        paddingLeft: 12,
        paddingTop: 12,
        width: "100%",
        alignItems: "center",
        verticalAlign: "top",
    },
    actionButton: {
        marginTop: 3,
        height: 30,
    },
    tree: {
        paddingTop: 7.75,
        paddingBottom: 7.75,
        height: "100%",
        maxWidth: "100%",
        backgroundColor: theme.palette.background.paper,
        boxShadow: "1px 1px 13px 0px #00000012",
        width: "100%",
        flexGrow: 0,
    },
    layerIcon: {
        display: "inline-flex",
        alignItems: "center",
    },
    wrapper: {
        display: "inline-flex",
        flexWrap: "nowrap",
        width: "100%",
        maxWidth: "100%",
        alignItems: "center",
        height: rowHeight,
    },
    first: {
        paddingRight: "10px",
        maxWidth: 340,
    },
    date: {
        maxWidth: 150,
    },
    fluid: {
        flex: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minWidth: "50px",
        " & p ": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
    },
    fluidContent: {
        flexDirection: "column",
    },
    noResult: {
        display: "inline-flex",
        alignItems: "center",
        height: 40,
    },
    checkbox: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: rowHeight,
        height: rowHeight,
    },
    chipContent: {
        flexGrow: 4,
        maxWidth: 450,
        display: "inline-block",
        whiteSpace: "nowrap",
    },
}));

function getIcon(entity: any, classes: any) {
    var fileName = getFileIcon(entity);
    //console.log("fileName:", fileName);
    if (!fileName) {
        return <File className={classes.icon} />;
    }
    const Icon = icons[fileName];
    return (
        <div className={classes.layerIcon}>
            <Icon className={classes.icon} />
        </div>
    );
}

function MainPanel(props: { setSelectedItem: (id: string) => void }) {
    const { classes: common } = commonStyles();
    const { classes, cx } = useStyles();
    const { classes: viewsClasses } = viewsStyles();
    const { setSelectedItem } = props;
    const data: any = getCachedData();
    const entities = data["entities"];
    const projects = data["projects"];

    const [reload, setReload] = useState(false);
    const [count, setCount] = useState(0);
    const [list, setList] = useState<any>(Object.entries(entities));
    const [checkItems, setCheckedItems] = useState<string[]>([]);
    const { 
        defaultProjects,
        demoProjectsHidden
    } = useContext<any>(PendingContext);

    var currentList: any = [];
    //console.log("count:", count);
    useEffect(() => {
        var updateFilter = (filter: string, isReload?: boolean) => {
            var newList: any = [...Object.entries(entities)];
            if (filter !== "all") {
                newList = newList.filter((entries: any) => {
                    return entries[0] === filter;
                });
            }
            
            newList = newList.filter((entry: any) => /*!demoProjectsHidden || */!defaultProjects.includes(entry[0])) 
            setList(newList);
            if (!isReload) {
                setReload(true);
            }
        };
        EventsEmitter.on("updateFilter", updateFilter);
        return () => {
            EventsEmitter.removeEventListener("updateFilter", updateFilter);
        };
    }, [reload, entities]);

    useEffect(() => {
        var reload = () => {
            if (data.loaded) {
                setCount((prevCount: number) => prevCount + 1);
            }
        };
        EventsEmitter.on("signalr_update", reload);
        EventsEmitter.on("dataUpdate", reload);
        return () => {
            EventsEmitter.removeEventListener("signalr_update", reload);
            EventsEmitter.removeEventListener("dataUpdate", reload);
        };
    }, [data]);

    useEffect(() => {
        const filter = Utils.StorageUtils.loadFromSessionStorage("filter") || "all";
        let newList: any = [...Object.entries(entities)];
        if (filter !== "all") {
            newList = newList.filter((entries: any) => {
                return entries[0] === filter;
            });
        }
        newList = newList.filter((entry: any) => /*!demoProjectsHidden || */!defaultProjects.includes(entry[0])) 

        setList(newList);
    }, [entities]);

    const handleCheckboxChange = (entity: any, checked: boolean) => {
        const itemsRef = checkItems;
        checked ? itemsRef.push(entity.ID) : itemsRef.splice(itemsRef.indexOf(entity.ID), 1);
        setCheckedItems(itemsRef);
        EventsEmitter.emit("updateCatalogList", checkItems);
    };

    const define_status = (str: string) => {
        return typeof str === "string" && str.length > 0 ? str : "Pending processing...";
    };

    if (!data.loaded) {
        return <CircularProgress />;
    }

    return (
        <div className={viewsClasses.main} style={{ filter: count > 0 ? "none" : "none" }}>
            <Scrollbars
                autoHeight
                autoHeightMax={"calc(100vh - " + (viewsHeaderHeight + headerHeight + 100) + "px)"}
                autoHide
                renderTrackHorizontal={(props: any) => (
                    <div {...props} style={{ display: "none" }} className="track-horizontal" />
                )}
            >
                <SimpleTreeView className={classes.tree} defaultExpandedItems={["1"]} multiSelect>
                    {list.map((entry: any, index: number) => {
                        var id = entry[0];
                        var pending: any = Object.values(entry[1]).filter((p: any) => {
                            return p.state === "PARTIAL" && p.type === "FILE" && p.name;
                        });
                        pending = Utils.sortArrayByProp(pending, "file_date");
                        const isDefaultProject = defaultProjects.includes(id);
                        const projectAccessLevel = getAccessLevel(id);
                        const hasFullAccess = projectAccessLevel === 'full';

                        if (projects[id] && pending.length > 0) {
                            currentList.push(projects[id]);
                            return (
                                <StyledTreeItem
                                    nodeId={"project-" + index}
                                    label={
                                        <div className={classes.wrapper}>
                                            <Public
                                                className={common.icon}
                                                style={{
                                                    color: folderColor,
                                                }}
                                            />
                                            <div className={classes.fluid}>
                                                <div className={classes.fluidContent}>
                                                    <Typography
                                                        variant="body2"
                                                        className={cx(common.mediumLabel, classes.fluidContent)}
                                                    >
                                                        {projects[id].name}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    key={"project-" + index}
                                >
                                    {pending.map((p: any, index1: number) => {
                                        var pendingStatus =
                                            typeof p["partial_status"] === "string" && p["partial_status"].length > 0
                                                ? define_status(p["partial_status"])
                                                : "Waiting in queue...";
                                        var isProgress =
                                            typeof p["partial_status"] === "string" && p["partial_status"].length > 0;
                                        var progress = parseInt(p["partial_progress"]) || 0;
                                        var strPath = FoldersActions.getPathFromEntity(p);
                                        var workflow: any = FoldersActions.getWorkflowFromEntity(p);
                                        let strPathShort = strPath;
                                        if (strPath && strPath.length > 50) {
                                            strPathShort = strPath.substring(0, 45) + "...";
                                        }
                                        // let products =
                                        //     workflow && workflow !== null && workflow["products"].join(" - ");

                                        let products = workflow?.products?.join(" - ") ?? "";

                                        let productsShort = products;
                                        if (products && products !== null && products.length > 50) {
                                            productsShort = products.substring(0, 45) + "...";
                                        }
                                        return (
                                            <StyledTreeItem
                                                nodeId={"pending-" + id + "-" + index1}
                                                onClick={() => setSelectedItem(p.ID)}
                                                label={
                                                    <div className={classes.wrapper}>
                                                        <div className={classes.checkbox}>
                                                            <Checkbox
                                                                edge="start"
                                                                tabIndex={-1}
                                                                disableRipple
                                                                disabled={isDefaultProject  || !hasFullAccess}
                                                                checked={checkItems.includes(p.ID) && hasFullAccess && !isDefaultProject}
                                                                onChange={(_e, checked) => {
                                                                    if (isDefaultProject || !hasFullAccess) return;
                                                                    handleCheckboxChange(p, checked)
                                                                }}
                                                                inputProps={{
                                                                    "aria-labelledby": `checkbox-list-label-${index}`,
                                                                }}
                                                            />
                                                        </div>
                                                        {getIcon(p, common)}
                                                        <div className={cx(classes.fluid, classes.first)}>
                                                            <Tooltip title={p.name}>
                                                                <Typography
                                                                    variant="body2"
                                                                    className={common.mediumLabel}
                                                                >
                                                                    {p.name}
                                                                </Typography>
                                                            </Tooltip>
                                                        </div>
                                                        <div className={classes.fluid}>
                                                            <Typography
                                                                variant="body2"
                                                                className={cx(common.mediumLabel, classes.date)}
                                                            >
                                                                {Utils.formatDateWithTime(p.file_date)}
                                                            </Typography>
                                                        </div>
                                                        { useVersioning && (
                                                            <div className={classes.fluid}>
                                                                <div className={classes.chipContent}>
                                                                    {workflow === null ? (
                                                                        <div>
                                                                            <Chip
                                                                                label="Not Versioned"
                                                                                size="small"
                                                                                style={{ marginLeft: 7 }}
                                                                            />
                                                                            <Tooltip title={strPath}>
                                                                                <Chip
                                                                                    label={strPathShort}
                                                                                    size="small"
                                                                                    style={{ marginLeft: 7 }}
                                                                                />
                                                                            </Tooltip>
                                                                        </div>
                                                                    ) : (
                                                                        <div>
                                                                            <Chip
                                                                                label={"Version: " + workflow["version"]}
                                                                                size="small"
                                                                                style={{
                                                                                    marginLeft: 7,
                                                                                    backgroundColor: folderColor,
                                                                                }}
                                                                            />
                                                                            <Tooltip title={products}>
                                                                                <Chip
                                                                                    label={productsShort}
                                                                                    size="small"
                                                                                    style={{ marginLeft: 7 }}
                                                                                />
                                                                            </Tooltip>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div
                                                            className={classes.fluid}
                                                            style={{
                                                                paddingLeft: "15px",
                                                            }}
                                                        >
                                                            {isProgress ? (
                                                                <div className={cx(classes.fluid, classes.wrapper)}>
                                                                    <CircularProgressWithLabel
                                                                        value={progress}
                                                                        color="primary"
                                                                        style={{
                                                                            width: 24,
                                                                            height: 24,
                                                                        }}
                                                                    />
                                                                    <Tooltip title={pendingStatus}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            style={{
                                                                                paddingLeft: 10,
                                                                            }}
                                                                            className={common.mediumLabel}
                                                                        >
                                                                            {pendingStatus}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                </div>
                                                            ) : (
                                                                <div className={classes.fluid}>
                                                                    <Tooltip title={pendingStatus}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            className={common.mediumLabel}
                                                                        >
                                                                            {pendingStatus}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                }
                                                key={"pending-" + index1}
                                            />
                                        );
                                    })}
                                </StyledTreeItem>
                            );
                        }
                        return <div key={"project-" + index} />;
                    })}
                    {currentList.length === 0 ? (
                        <TreeItem className={cx(common.mediumLabel, classes.noResult)} itemId="2" label="No Task" />
                    ) : (
                        ""
                    )}
                </SimpleTreeView>
            </Scrollbars>
        </div>
    );
}

const SidePanelMenuItem = ({ icon, label, onClick, disabled = false, id = "" }: { icon?:React.ReactNode; label: string; onClick: (e: any) => void; disabled?: boolean; id?: string}) => {
    return (
      <MenuItem disabled={disabled} disableRipple sx={{ p: 0, mb: 2 }}>
        <Link underline={'none'} onClick={onClick} {...(id ? { id} : {})}>
          {icon && <ListItemIcon sx={{ color: 'inherit' }}>{icon}</ListItemIcon>}
          <ListItemText sx={{textTransform:'uppercase', fontWeight: 600}}>{label}</ListItemText>
        </Link>
      </MenuItem>
    );
};

function SidePanel(props: { setOpen: any; selectedItem: string }) {
    const { classes } = useStyles();
    const { classes: viewsClasses } = viewsStyles();
    const history = useHistory();
    const { setOpen, selectedItem } = props;
    const { 
        //hasFullAccess,
        sweetAlerts
    } = useContext<any>(PendingContext);

    const goToFolder = (evt: any) => {
        EventsEmitter.emit("changeTab", "catalog");
        history.push("/catalog/" + selectedItem);
        history.goForward();
    };

    const deleteItems = (evt: any) => {
        /*if (!hasFullAccess) {
            sweetAlerts();
            return;
        }*/
        setOpen("deleteItems");
    };

    return (
        <div className={viewsClasses.side}>
            <div className={classes.header}>
                <StyledMenu sx={{ mb: 2 }}>
                    <SidePanelMenuItem
                        label="Show in Catalog"
                        icon={<FolderIcon fontSize={'small'} /> }
                        onClick={goToFolder}
                        disabled={selectedItem === ""}
                    />
                    { 
                    //false && (
                        <SidePanelMenuItem
                            label="Delete"
                            icon={<DeleteIcon fontSize={'small'} /> }
                            onClick={deleteItems}
                            disabled={FoldersActions.selectedItems.length === 0}
                        />
                    //)
                    }
                </StyledMenu>
            </div>
        </div>
    );
}

export const PendingContext = createContext({});

export default function Pending() {
    const { classes: common, cx } = commonStyles();
    const { classes: viewsClasses } = viewsStyles();
    const data: any = getCachedData();
    const history = useHistory();
    const [filter, setFilter] = useState(StorageUtils.loadFromSessionStorage("filter") || "all");
    const [loadData, setLoadData] = useState(false);
    const [selectedItem, setSelectedItem] = useState("");
    const [open, setOpen] = useState("");
    const [update, setUpdate] = useState(false);
    const [progress, setProgress] = useState(0);
	const storeAuthData = useSelector((state: any) => state.auth);
    const [hasFullAccess, setHasFullAccess] = useState(false);
    const [onlyDefaultProjects, setOnlyDefaultProjects] = useState(false);
    const { hasActiveSubscription = false, projectAccessLevel:access_level = "read", defaultProjects } = storeAuthData;
    const [projects, setProjects] = useState<any>(null);
    const demoProjectsHidden = Number(localStorage.getItem('hideDemoProjects') || 0) > 0;
    
    const Swal = useSwalWrapper();

    const sweetAlerts = () => {
        Swal.fire({
        title: 'Your account has limited access!',
        text: 'We have limited your access to some features. Please upgrade your current plan to access this feature.',
        imageWidth: 450,
        imageHeight: 200,
        confirmButtonText: "Upgrade my plan",
        showCloseButton: true,
        }).then((result) => {
            result.isConfirmed && history.push("/profile");
        });
    };

    useEffect(() => {
        setHasFullAccess(hasActiveSubscription && access_level === 'full')
    }, [access_level, hasActiveSubscription])

    useEffect(() => {
        const _projects = JSON.parse(JSON.stringify(data["projects"]));
        if (_.isEqual(projects, _projects)) return;

        setProjects(_projects)
    }, [data.projects])

    useEffect(() => {
        if (!projects) return;
        const userProjects = Object.keys(projects).filter((item: string) => !defaultProjects.includes(item))
        setOnlyDefaultProjects(userProjects.length < 1);
    }, [projects, defaultProjects])

    useEffect(() => {
        const updateFilter = (filter: string) => {
            setFilter(filter);
        };

        const handleListBoxSelectedChange = (selectedItems: string[]) => {
            FoldersActions.selectedItems = selectedItems;
            setUpdate((prevUpdate) => !prevUpdate);
        };

        const handleProgress = (curProgress: number) => {
            setProgress(curProgress);
        };

        if (data.loaded) {
            FoldersActions.buildFolderTree(filter, "0", () => {});
            setLoadData(true);
        }
        EventsEmitter.on("updateFilter", updateFilter);
        EventsEmitter.on("projects_progress", handleProgress);
        EventsEmitter.on("updateCatalogList", handleListBoxSelectedChange);
        return () => {
            EventsEmitter.removeEventListener("updateCatalogList", handleListBoxSelectedChange);
            EventsEmitter.removeEventListener("projects_progress", handleProgress);
            EventsEmitter.removeEventListener("updateFilter", updateFilter);
        };
    }, [data, filter]);

    useEffect(() => {
        return () => {
            FoldersActions.selectedItems = [];
        };
    }, []);

    if (!data.loaded || !loadData) {
        return progress === 0 ? <CircularProgress /> : <CircularProgressWithLabel value={progress} />;
    }

    const handleClose = () => {
        setOpen("");
    };
    const PendingContextValue = {
        hasFullAccess,
        defaultProjects,
        onlyDefaultProjects,
        sweetAlerts,
        demoProjectsHidden
    }

    return (
        <PendingContext.Provider value={PendingContextValue}>
            <div className={viewsClasses.main} style={{ filter: update ? "none" : "none", width: "100%", height: "100%" }}>
                <Div className={viewsClasses.content} sx={{flexDirection: {xs: 'column', sm: 'column', md: 'row', lg: 'row', xl: 'row'}}}>
                    <MainPanel setSelectedItem={setSelectedItem} />
                    <SidePanel setOpen={setOpen} selectedItem={selectedItem} />
                </Div>

                <DeleteItemsPanel open={open === "deleteItems"} handleClose={handleClose} />
            </div>
        </PendingContext.Provider>
    );
}
